/* eslint-disable no-param-reassign */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { SiteTheme } from '../../../../../theme';

import { appConfig } from '../../../../../appConfig';
import { isProductionEnvironment } from '../../../../../utility/environments';
import persistance from '../../../persistance/persistance';

const getInitialState = () =>
    ({
        ...appConfig.coopSettings.featureFlags,
        enableFullSpaMode:
            persistance.cookieBasedFeatureToggles.isFullSpaModeEnabled() &&
            !isProductionEnvironment,
    }) as FeatureFlags & {
        siteTheme: SiteTheme;
        enableFullSpaMode: boolean;
    };

type OnlyBoolean<T> = { [K in keyof T]: T[K] extends boolean ? K : never }[keyof T];
type OnlyBooleanFlags = Exclude<OnlyBoolean<ReturnType<typeof getInitialState>>, undefined>;

type NonBooleanFlags = Exclude<keyof ReturnType<typeof getInitialState>, OnlyBooleanFlags>;

const slice = createSlice({
    name: 'featureFlags',
    initialState: getInitialState(),
    reducers: {
        setDynamic: (state, action: PayloadAction<{ name: string; enabled: boolean }>) => {
            const { name, enabled } = action.payload;

            if (!state.dynamicFeatureFlags) {
                state.dynamicFeatureFlags = [];
            }

            const index = state.dynamicFeatureFlags.findIndex((flag) => flag.name === name);
            if (index >= 0) {
                state.dynamicFeatureFlags[index].enabled = enabled;
            }
        },
        setFeatureFlag: <TKey extends OnlyBooleanFlags | NonBooleanFlags>(
            state: ReturnType<typeof getInitialState>,
            action: PayloadAction<{ key: TKey; value: ReturnType<typeof getInitialState>[TKey] }>,
        ) => {
            state[action.payload.key] = action.payload.value;
        },
        setFeatureFlagNotProd: <TKey extends OnlyBooleanFlags | NonBooleanFlags>(
            state: ReturnType<typeof getInitialState>,
            action: PayloadAction<{ key: TKey; value: ReturnType<typeof getInitialState>[TKey] }>,
        ) => {
            if (isProductionEnvironment) {
                // eslint-disable-next-line no-console
                console.warn(
                    `Attempted to set feature flag ${action.payload.key} in production environment. This action is ignored.`,
                );
                return state;
            }

            state[action.payload.key] = action.payload.value;
        },
    },
});

export const featureFlagsActions = slice.actions;

export default slice.reducer;
